import { useEffect, useState } from "react";
import { useModal } from "../context/ModalContext";
import {
  getFinancialDetailsById,
  saveFinancialDetails,
  updateFinancialDetails,
} from "../services/financialService";
import {
  Trash2,
  Save,
  Loader2,
  ArrowLeft,
  ChevronsRight,
  Plus,
} from "lucide-react";
import FormButton from "./FormActionButtons";
import Loading from "./Loading";
import { useToast } from "../context/ToastContext";
import FormNavigationButtons from "./FormNavigationButtons";

function FinancialAbility({
  companyId,
  versionId,
  onNext,
  onBack,
  isEditable,
  currentStep,
  totalSteps,
}) {
  const [saving, setSaving] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fallbackUsed, setFallbackUsed] = useState(false);
  const [formData, setFormData] = useState({
    annualTurnOver: 0,
    vatNo: "",
    registrationNumber: "",
    documents: [],
    bank: {
      bankName: "",
      bankAccountNumber: "",
      bankAddress: "",
      historyOfDebts: "",
    },
    id: 0,
    version: versionId,
  });

  const { showModal } = useModal();
  const toast = useToast();

  const fetchData = async () => {
    setLoading(true);
    try {
      let fetchVersion = formData.version;
      let isFallback = false;

      let financial = await getFinancialDetailsById(companyId, fetchVersion);

      // 2. If no data found, and version > 1, fallback to previous version
      if ((!financial || financial.length === 0) && fetchVersion > 1) {
        fetchVersion = fetchVersion - 1;
        isFallback = true;
        financial = await getFinancialDetailsById(companyId, fetchVersion);
        setFallbackUsed(true);
      }

      if (financial.length > 0) {
        const entry = financial[0];
        const bank = entry.bankDetails?.[0] || {};

        const formData = {
          id: isFallback ? 0 : entry.id,
          companyId: entry.companyId,
          version: entry.version,
          annualTurnOver: entry.annualTurnOver || "",
          vatNo: entry.vatNo || "",
          registrationNumber: entry.registrationNumber || "",
          documents: entry.documents || [],
          bank: {
            bankName: bank.bankName || "",
            bankAccountNumber: bank.accountNumber || "",
            bankAddress: bank.address || "",
            historyOfDebts: bank.historyOfDebts || "",
          },
        };

        if (isFallback) {
          delete formData.id;
        }
        // setFormData({
        //   id: isFallback ? 0 : entry.id,
        //   companyId: entry.companyId,
        //   version: entry.version,
        //   annualTurnOver: entry.annualTurnOver || "",
        //   vatNo: entry.vatNo || "",
        //   registrationNumber: entry.registrationNumber || "",
        //   documents: entry.documents || [],
        //   bank: {
        //     bankName: bank.bankName || "",
        //     bankAccountNumber: bank.accountNumber || "",
        //     bankAddress: bank.address || "",
        //     historyOfDebts: bank.historyOfDebts || "",
        //   },
        // });
        setFormData(formData);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error("Failed to fetch financial data", err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [companyId, formData.version]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsChanged(true);
  };

  const handleBankChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      bank: {
        ...prev.bank,
        [field]: value,
      },
    }));
    setIsChanged(true);
  };

  const handleSaveFinancial = async (
    onSuccessNext = null,
    showPopup = true
  ) => {
    let payload = {
      id: formData.id,
      companyId: parseInt(companyId),
      version: versionId,
      annualTurnOver: formData.annualTurnOver,
      vatNo: formData.vatNo,
      registrationNumber: formData.registrationNumber,
      documents: formData.documents,
      bankDetails: [
        {
          bankName: formData.bank.bankName,
          bankAccountNumber: formData.bank.bankAccountNumber,
          bankAddress: formData.bank.bankAddress,
          historyOfDebts: formData.bank.historyOfDebts,
        },
      ],
    };
    setSaving(true);
    if (fallbackUsed) {
      payload.id = 0;
    }
    try {
      const res =
        payload.id === 0
          ? await saveFinancialDetails(payload)
          : await updateFinancialDetails(payload);

      if (!res.status) throw new Error("Save failed");

      if (showPopup) {
        // showModal(
        //   "Saved Successfully",
        //   "Financial ability saved successfully.",
        //   onSuccessNext
        // );
        toast.success("Financial ability saved successfully.");
      } else {
        if (onSuccessNext) onNext(); // Directly call onNext here
      }
      setIsChanged(false);
      setSaving(false);
    } catch (err) {
      setSaving(false);
      console.error("Save error:", err);
      // showModal("Save Failed", "Failed to save financial ability.");
      toast.error("Failed to save financial ability.");
    }
  };

  const renderInput = (
    label,
    value,
    onChange,
    placeholder = label,
    type = "text"
  ) => (
    <div className="sm:flex mb-3">
      <label className="block mb-1 text-sm sm:min-w-[150px]">{label}</label>
      <input
        type={type}
        className={`w-full px-4 py-2 border rounded text-sm ${
          !isEditable ? "bg-gray-100" : ""
        }`}
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          if (
            type === "number" &&
            e.target.value !== "" &&
            isNaN(e.target.value)
          )
            return;
          onChange(e.target.value);
        }}
        disabled={!isEditable}
      />
    </div>
  );

  const handleSaveOnly = async () => {
    await handleSaveFinancial();
  };

  const handleSaveAndContinue = async () => {
    await handleSaveFinancial(true, false); // Call onNext directly in handleSave
  };

  return loading ? (
    <Loading fullscreen={false} />
  ) : (
    <section className="px-10 py-6 bg-white">
      <h2 className="text-xl font-semibold mb-4">Financial Ability</h2>
      <div className="grid grid-cols-2 gap-4 mb-4">
        {renderInput(
          "Annual Turnover",
          formData.annualTurnOver,
          (val) => handleChange("annualTurnOver", val),
          "Annual Turnover",
          "number"
        )}

        {renderInput("TR / VAT no.", formData.vatNo, (val) =>
          handleChange("vatNo", val)
        )}
        {renderInput(
          "Company Registration No.",
          formData.registrationNumber,
          (val) => handleChange("registrationNumber", val)
        )}
      </div>

      <h2 className="text-xl font-semibold mt-6 mb-4">Bank Details</h2>
      <div className="grid grid-cols-2 gap-4 mb-4">
        {renderInput("Bank Name", formData.bank.bankName, (val) =>
          handleBankChange("bankName", val)
        )}
        {renderInput(
          "Account no.",
          formData.bank.bankAccountNumber,
          (val) => handleBankChange("bankAccountNumber", val),
          "Account no.",
          "number"
        )}
        {renderInput("Address", formData.bank.bankAddress, (val) =>
          handleBankChange("bankAddress", val)
        )}
        {renderInput("History of debts", formData.bank.historyOfDebts, (val) =>
          handleBankChange("historyOfDebts", val)
        )}
      </div>

      {/* --- Buttons Section using the new component --- */}
      <FormNavigationButtons
        onBack={onBack}
        onNext={onNext}
        handleSaveOnly={handleSaveOnly}
        handleSaveAndContinue={handleSaveAndContinue}
        isEditable={isEditable}
        isChanged={isChanged}
        saving={saving}
        fallbackUsed={fallbackUsed}
        currentStep={currentStep} // Pass current step
        totalSteps={totalSteps} // Pass total steps
      />
      {/* --- End Buttons Section --- */}

      {/* Buttons */}
      <div className="flex flex-wrap gap-4 justify-between sm:justify-end mt-8 hidden">
        <button
          type="button"
          onClick={onBack}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300 transition duration-200 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {isEditable && (
          <button
            type="button"
            onClick={handleSaveOnly}
            disabled={!isChanged || saving}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
              saving || !isChanged
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
            }`}
          >
            {saving ? (
              <Loader2 className="animate-spin w-4 h-4" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>
        )}

        {isEditable && (
          <button
            type="button"
            onClick={handleSaveAndContinue}
            disabled={!isChanged || saving}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
              saving || !isChanged
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700 text-white cursor-pointer"
            }`}
          >
            {saving ? (
              <Loader2 className="animate-spin w-4 h-4" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save & Continue
          </button>
        )}

        <button
          type="button"
          onClick={onNext}
          disabled={saving || (isEditable && isChanged && !fallbackUsed)}
          className={`flex items-center gap-2 px-6 py-2 rounded-lg transition duration-200 ${
            saving || (isEditable && isChanged && !fallbackUsed)
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-purple-600 hover:bg-purple-700 text-white cursor-pointer"
          }`}
        >
          <ChevronsRight className="w-4 h-4" />
          Next
        </button>
      </div>
    </section>
  );
}

export default FinancialAbility;
